import { useCallback, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { useAdditionalFieldsController } from 'api/controllers/AdditionalFieldsController';

import { CounterpartyDropDown, CounterpartyType, Invoice } from 'openapi';

import { useDebounce } from 'hooks/useDebounce';

import { COUNTERPARTY_SEARCH_MIN_CHARACTERS } from 'utils/constants/invoices';

export const useCounterpartySearch = (currentInvoice: Invoice) => {
  const { setValue, clearErrors } = useFormContext();
  const { getAllDropDownCounterpartiesByCompany } =
    useAdditionalFieldsController();

  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<CounterpartyDropDown[]>([]);
  const [searchParams, setSearchParams] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);

  const notEnoughCharactersToSearch =
    searchParams.length < COUNTERPARTY_SEARCH_MIN_CHARACTERS;

  const getCounterParties = useCallback(async () => {
    if (notEnoughCharactersToSearch || !currentInvoice.companyId) {
      setIsLoading(false);
      setOptions([]);
      return;
    }

    const result = await getAllDropDownCounterpartiesByCompany(
      Number(currentInvoice.companyId),
      CounterpartyType.SUPPLIER,
      searchParams
    );
    setOptions(result);
    setIsLoading(false);
  }, [
    getAllDropDownCounterpartiesByCompany,
    searchParams,
    currentInvoice.companyId,
    notEnoughCharactersToSearch
  ]);

  const debouncedSearch = useDebounce(getCounterParties, 500);

  useEffect(() => {
    setIsLoading(true);
    debouncedSearch();
  }, [searchParams, debouncedSearch]);

  const handleSelect = useCallback(
    (selectedOption?: CounterpartyDropDown) => {
      if (!selectedOption) {
        return;
      }

      setValue('counterpartyId', selectedOption.id || null);
      setValue('counterpartyName', selectedOption.name || '');
      setValue(
        'counterpartyRegistrationNumber',
        selectedOption.registrationNumber || ''
      );
      setValue('counterpartyVatNumber', selectedOption.vatNumber || '');
      setValue('isCounterpartyValidated', Boolean(selectedOption.isValidated));
      clearErrors();
    },
    [setValue, clearErrors]
  );

  return {
    open,
    setOpen,
    options,
    isLoading,
    setIsLoading,
    searchParams,
    setSearchParams,
    notEnoughCharactersToSearch,
    handleSelect
  };
};
